import {
  decryptE2e,
  encryptE2e,
  parseE2eEnvelope,
  serializeE2eEnvelope,
  type E2eRecipient,
} from "@link/crypto";

import type { CallSummary, Dm } from "./api";
import { loadIdentity } from "./identity";

const MEDIA_KEY_BYTES = 32;

export interface SealedCallMediaKey {
  keyContextId: string;
  keyEnvelopeB64: string;
}

/**
 * Generate a fresh call key and seal it separately to every DM participant's
 * identity public key. Only the serialized envelope is returned; the raw key
 * is wiped before this function resolves.
 */
export async function createCallMediaKey(dm: Dm): Promise<SealedCallMediaKey> {
  if (dm.participants.length < 2) {
    throw new Error("There is nobody else in this conversation.");
  }
  const recipients: E2eRecipient[] = dm.participants.map((participant) => ({
    accountIdHash: b64ToBytes(participant.account_id_hash_b64),
    identityPublicKey: b64ToBytes(participant.public_key_b64),
  }));
  const keyContextId = `call-media:${crypto.randomUUID()}`;
  const rawKey = crypto.getRandomValues(new Uint8Array(MEDIA_KEY_BYTES));
  try {
    const envelope = await encryptE2e({
      plaintext: rawKey,
      recipients,
      channelId: dm.xid,
      messageId: keyContextId,
    });
    return {
      keyContextId,
      keyEnvelopeB64: bytesToB64(serializeE2eEnvelope(envelope)),
    };
  } finally {
    rawKey.fill(0);
  }
}

/** Recover the per-call media key locally. The server never receives this. */
export async function openCallMediaKey(
  call: CallSummary,
  ownAccountXid: string,
): Promise<Uint8Array> {
  const identity = await loadIdentity(ownAccountXid);
  if (identity === null) {
    throw new Error("Your local identity key is not loaded. Sign in again before joining the call.");
  }
  const rawKey = await decryptE2e({
    envelope: parseE2eEnvelope(b64ToBytes(call.key_envelope_b64)),
    recipientPrivateKey: identity.ecdhPrivateKey,
    recipientAccountIdHash: identity.publicKeyHash,
    channelId: call.dm_xid,
    messageId: call.key_context_id,
  });
  if (rawKey.byteLength !== MEDIA_KEY_BYTES) {
    rawKey.fill(0);
    throw new Error("The call encryption key is invalid.");
  }
  return rawKey;
}

function bytesToB64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function b64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
